/**
 * PATH: packages/job-hub/src/jobs/qa/qaHoldPoints.ts
 * FILE_ID: JOBHUB-QA-HOLD-POINTS-001
 * PURPOSE: Pure reads of a check's sign-off state from the area payload.
 * LANDMINE: has_hold_point means PARKED (hidden from Run and the ITP, change
 *      #707). Sign-off is hold_point_config.signoff_required plus the area's
 *      hold_points rows; never read one as the other.
 */
import type { QaHoldPoint, QaHoldPointSignature, QaItemInstance } from './qaTypes'

export type QaSignoffState = 'none' | 'needed' | 'requested' | 'approved' | 'rejected'

export interface QaItemSignoff {
  state: QaSignoffState
  /** The newest hold point on the check; null until one is requested. */
  holdPoint: QaHoldPoint | null
  signature: QaHoldPointSignature | null
}

export function qaNeedsSignoff(item: Pick<QaItemInstance, 'hold_point_config'>): boolean {
  return Boolean(item.hold_point_config?.signoff_required)
}

function holdPointTime(row: QaHoldPoint): string {
  return row.requested_at || row.created_at || ''
}

/** Newest first, so a re-request after a rejection wins. */
export function latestItemHoldPoint(
  holdPoints: readonly QaHoldPoint[] | undefined,
  itemId: string,
): QaHoldPoint | null {
  let latest: QaHoldPoint | null = null
  for (const row of holdPoints ?? []) {
    if (row.item_instance !== itemId) continue
    if (!latest || holdPointTime(row) > holdPointTime(latest)) latest = row
  }
  return latest
}

export function qaItemSignoff(
  item: Pick<QaItemInstance, 'id' | 'hold_point_config' | 'has_hold_point'>,
  holdPoints: readonly QaHoldPoint[] | undefined,
): QaItemSignoff {
  const holdPoint = latestItemHoldPoint(holdPoints, item.id)
  const signature = holdPoint?.signatures?.[0] ?? null
  if (holdPoint && holdPoint.status !== 'not_requested') {
    return { state: holdPoint.status, holdPoint, signature }
  }
  const needed = qaNeedsSignoff(item) || Boolean(item.has_hold_point)
  return { state: needed ? 'needed' : 'none', holdPoint, signature }
}
